import { useEffect, useMemo, useState } from "react";

import {
  DEFAULT_SHOP_SORT,
  type ShopSort,
  filterShopItems,
  getShopFilterCategories,
  shouldGroupShopItems,
  sortShopItems,
} from "@/lib/shop-catalog-view";

import { ShopCatalogResponse } from "@/types/shop";

export interface ShopCatalogViewInitialState {
  category?: string | null;
  sort?: ShopSort;
}

export function useShopCatalogView(
  catalog: ShopCatalogResponse,
  initialState: ShopCatalogViewInitialState = {},
) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    initialState.category ?? undefined,
  );
  const [sort, setSort] = useState<ShopSort>(
    initialState.sort ?? DEFAULT_SHOP_SORT,
  );

  const shouldGroupItems = shouldGroupShopItems(catalog.items.length);
  // An empty catalog is still loading; keep a category carried in from the URL.
  const catalogLoaded = catalog.items.length > 0;
  useEffect(() => {
    if (catalogLoaded && !shouldGroupItems) setSelectedCategory(undefined);
  }, [catalogLoaded, shouldGroupItems]);
  const effectiveSelectedCategory = shouldGroupItems
    ? selectedCategory
    : undefined;
  const categories = useMemo(
    () => getShopFilterCategories(catalog.items, catalog.categories),
    [catalog.categories, catalog.items],
  );
  const filteredItems = useMemo(
    () =>
      sortShopItems(
        filterShopItems(catalog.items, {
          searchQuery,
          selectedCategory: effectiveSelectedCategory,
          categories: catalog.categories,
        }),
        sort,
      ),
    [
      catalog.categories,
      catalog.items,
      searchQuery,
      effectiveSelectedCategory,
      sort,
    ],
  );
  const hasActiveFilters = Boolean(
    searchQuery.trim() || effectiveSelectedCategory,
  );

  return {
    state: {
      searchQuery,
      selectedCategory: effectiveSelectedCategory,
      sort,
    },
    actions: {
      setSearchQuery,
      setSelectedCategory,
      setSort,
      clearFilters: () => {
        setSearchQuery("");
        setSelectedCategory(undefined);
      },
    },
    derived: {
      filteredItems,
      categories,
      resultCount: filteredItems.length,
      hasActiveFilters,
      emptyStateReason:
        hasActiveFilters && filteredItems.length === 0 ? "filters" : null,
      showSearch: catalog.items.length > 20,
      // A category from a link stays changeable, so there's always a way back to everything.
      showCategories:
        shouldGroupItems &&
        (categories.length > 2 || Boolean(effectiveSelectedCategory)),
      showSort: catalog.items.length > 1,
    },
  };
}

export type ShopCatalogView = ReturnType<typeof useShopCatalogView>;
